import React, { useEffect, useRef, useState } from 'react'
import { Button, Modal } from '@climate/components'

interface MemberAgreementModalProps {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  /** Rendered Member Agreement HTML, served by the entities#member_agreement endpoint. */
  html?: string
  onAgree: () => Promise<void> | void
}

/**
 * Click-to-agree dialog for the Member Agreement.
 *
 * The "Agreed" checkbox only enables once the member has scrolled to the end of
 * the document: an acceptance is only defensible if the terms were actually put
 * in front of the person accepting them.
 */
export default function MemberAgreementModal({ isOpen, setIsOpen, html, onAgree }: MemberAgreementModalProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [hasReadToEnd, setHasReadToEnd] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setHasReadToEnd(false)
      setAgreed(false)
      setSubmitting(false)
    }
  }, [isOpen])

  // A document short enough not to overflow its pane has already been read in
  // full, so it must not be left permanently un-agreeable.
  useEffect(() => {
    const pane = scrollRef.current
    if (!isOpen || !pane || !html) return
    if (pane.scrollHeight <= pane.clientHeight + 1) setHasReadToEnd(true)
  }, [isOpen, html])

  const handleScroll = () => {
    const pane = scrollRef.current
    if (!pane) return
    if (pane.scrollTop + pane.clientHeight >= pane.scrollHeight - 24) setHasReadToEnd(true)
  }

  const submit = async () => {
    setSubmitting(true)
    try {
      await onAgree()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} wrapClasses="sm:max-w-5xl" overflowHidden={false}>
      <h1 id="member_agreement_title" className="text-2xl font-bold mb-4">
        Tellus Climate Suite Member Agreement
      </h1>

      <div
        id="member_agreement_document"
        ref={scrollRef}
        onScroll={handleScroll}
        className="max-h-[60vh] overflow-y-auto border border-gray-200 rounded-md p-4 text-sm"
      >
        {html
          ? <div dangerouslySetInnerHTML={{ __html: html }} />
          : <p className="text-gray-500">Loading the Member Agreement...</p>}
      </div>

      {!hasReadToEnd && (
        <p id="member_agreement_scroll_hint" className="mt-3 text-sm text-gray-500">
          Please scroll to the end of the agreement to continue.
        </p>
      )}

      <div className="mt-4 flex items-start">
        <input
          id="member_agreement_agreed"
          type="checkbox"
          checked={agreed}
          disabled={!hasReadToEnd}
          onChange={(e) => setAgreed(e.target.checked)}
          className="h-4 w-4 mt-0.5 rounded-sm border-gray-300 text-bonfire-dark-light focus:text-bonfire-dark-light disabled:opacity-50"
        />
        <label
          htmlFor="member_agreement_agreed"
          className={`ml-2 text-sm font-medium ${hasReadToEnd ? 'text-gray-700' : 'text-gray-400'}`}
        >
          Agreed
        </label>
      </div>

      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
        <Button id="member_agreement_submit" disabled={!agreed || submitting} onClick={() => submit()}>
          Agree &amp; Submit
        </Button>
        <Button id="member_agreement_cancel" secondary onClick={() => setIsOpen(false)}>
          Cancel
        </Button>
      </div>
    </Modal>
  )
}
